import { Component, isValidElement, useState } from 'react';
import type { ComponentType, PropsWithChildren, PropsWithRef } from 'react';

import type { ErrorBoundaryProps, ErrorBoundaryState } from './type';

const changedArray = (a: any[] = [], b: any[] = []) =>
  a.length !== b.length || a.some((item, index) => !Object.is(item, b[index]));

const initialState: ErrorBoundaryState = { error: null, info: null };

class ErrorBoundary extends Component<
  PropsWithRef<PropsWithChildren<ErrorBoundaryProps>>,
  ErrorBoundaryState
> {
  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  state = initialState;

  public resetErrorBoundary = (...args: any[]) => {
    this.props.onReset?.(...args);
    this.reset();
  };

  public reset() {
    this.setState(initialState);
  }

  public componentDidCatch(error: Error, info: { componentStack: string }) {
    this.props.onError?.(error, info);
    this.setState({ error, info });
  }

  public componentDidUpdate(
    prevProps: ErrorBoundaryProps,
    prevState: ErrorBoundaryState,
  ) {
    const { error } = this.state;
    const { resetKeys } = this.props;

    if (
      error !== null &&
      prevState.error !== null &&
      changedArray(prevProps.resetKeys, resetKeys)
    ) {
      this.props.onResetKeysChange?.(prevProps.resetKeys, resetKeys);
      this.reset();
    }
  }

  public render() {
    const { error, info } = this.state;

    const { fallbackRender, FallbackComponent, fallback } = this.props;

    if (error !== null) {
      const props = {
        error,
        info,
        resetErrorBoundary: this.resetErrorBoundary,
      };
      if (isValidElement(fallback)) {
        return fallback;
      } else if (typeof fallbackRender === 'function') {
        return fallbackRender(props);
      } else if (FallbackComponent) {
        return <FallbackComponent {...props} />;
      } else {
        throw new Error(
          'react-error-boundary requires either a fallback, fallbackRender, or FallbackComponent prop',
        );
      }
    }

    return this.props.children;
  }
}

export function withErrorBoundary<P extends React.JSX.Element>(
  Component: ComponentType<P>,
  errorBoundaryProps: ErrorBoundaryProps,
): ComponentType<P> {
  const Wrapped: ComponentType<P> = props => {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };

  const name = Component.displayName || Component.name || 'Unknown';
  Wrapped.displayName = `withErrorBoundary(${name})`;

  return Wrapped;
}

export function useErrorHandler(
  givenError?: unknown,
): (error: unknown) => void {
  const [error, setError] = useState<unknown>(null);
  if (givenError != null) throw givenError;
  if (error != null) throw error;
  return setError;
}

export default ErrorBoundary;
